/* ============================================================
   Financeability Pack

   Produces the one-pager an operator should be able to put in front of
   a lender: the cash cycle, the working capital gap it creates, debt
   service coverage, and an honest read of how the file will be
   received.

   Not a credit decision, and it says so. The value is in walking into
   the meeting already knowing the answer to the first four questions.
   ============================================================ */

import {
  assessFinanceability,
  LENDER_CHECKLIST,
  REMEDIES,
  DIMENSIONS
} from '../../data/finance.js';
import { html, raw } from '../../shared/html.js';
import { icon } from '../../shared/icons.js';
import { money, number, percent, parseNumber } from '../../shared/format.js';
import { saveRecord } from '../../shared/records.js';
import { prefillForm, rememberProfile } from '../../shared/profile.js';
import { PrefillNotice } from '../../components/PrefillNotice.js';
import { PrintButton } from '../../components/PrintButton.js';
import { BoardPreamble, boardFootnote } from '../../components/BoardPreamble.js';
import { FINANCEABILITY_REGISTER } from '../../data/board-pack.js';
import { readable } from '../../shared/tone.js';

export function FinanceabilityPage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  page.innerHTML = html`
    <header class="page-head">
      <h1>Financeability Pack</h1>
      <p>
        What a lender will see before they say so. Cash cycle, working capital gap, debt service
        coverage — and which single dimension is holding the file back.
      </p>
    </header>

    <div id="fin-form">
      <section class="section">
        <h2 class="section-title">Trading</h2>
        <div class="card">
          <div class="grid-2">
            <label class="field">
              <span class="field-label">Annual revenue (USD)</span>
              <input id="fin-revenue" class="input-field num" type="number" inputmode="decimal"
                     min="0" step="1000" placeholder="850000" />
            </label>
            <label class="field">
              <span class="field-label">Annual EBITDA (USD)</span>
              <input id="fin-ebitda" class="input-field num" type="number" inputmode="decimal"
                     step="1000" placeholder="140000" />
              <span class="field-hint">Earnings before interest, tax, depreciation and amortisation.</span>
            </label>
          </div>
          <label class="field" style="margin-bottom:0;">
            <span class="field-label">Largest customer, share of revenue (%)</span>
            <input id="fin-concentration" class="input-field num" type="number" inputmode="numeric"
                   min="0" max="100" step="1" placeholder="35" />
          </label>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">The cash cycle</h2>
        <div class="card">
          <div class="grid-2">
            <label class="field">
              <span class="field-label">Debtor days (DSO)</span>
              <input id="fin-dso" class="input-field num" type="number" inputmode="numeric"
                     min="0" step="1" placeholder="75" />
              <span class="field-hint">Invoice to payment received.</span>
            </label>
            <label class="field">
              <span class="field-label">Creditor days (DPO)</span>
              <input id="fin-dpo" class="input-field num" type="number" inputmode="numeric"
                     min="0" step="1" placeholder="30" />
              <span class="field-hint">How long your own suppliers give you.</span>
            </label>
          </div>
          <label class="field" style="margin-bottom:0;">
            <span class="field-label">Duty float days</span>
            <input id="fin-duty-float" class="input-field num" type="number" inputmode="numeric"
                   min="0" step="1" placeholder="10" />
            <span class="field-hint">
              Duty paid on arrival to the customer settling it. This is the piece a generic cash
              cycle misses, and usually the piece that hurts most.
            </span>
          </label>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">Debt &amp; assets</h2>
        <div class="card">
          <div class="grid-2">
            <label class="field">
              <span class="field-label">Annual debt service (USD)</span>
              <input id="fin-debt-service" class="input-field num" type="number" inputmode="decimal"
                     min="0" step="1000" placeholder="90000" />
              <span class="field-hint">Principal plus interest across all facilities.</span>
            </label>
            <label class="field">
              <span class="field-label">Average fleet age (years)</span>
              <input id="fin-fleet-age" class="input-field num" type="number" inputmode="decimal"
                     min="0" step="0.5" placeholder="9" />
            </label>
          </div>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">What you can already produce</h2>
        <div class="card">
          <div class="stack" style="gap: var(--ca-space-3);">
            ${LENDER_CHECKLIST.map(
              (item) => html`
                <label class="row" style="gap: var(--ca-space-3); cursor: pointer; align-items: flex-start;">
                  <input type="checkbox" id="fin-${item.id}"
                         style="margin-top: 3px; width: 17px; height: 17px; accent-color: var(--brand-accent);" />
                  <span style="flex:1;">
                    <span style="display:block; font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">
                      ${item.label}
                    </span>
                    <span style="display:block; font-size: var(--ca-text-2xs); color: var(--ca-text-quaternary);">
                      ${item.weight}
                    </span>
                  </span>
                </label>
              `
            )}
          </div>
        </div>
      </section>

      <div id="fin-error"></div>

      <button class="btn btn-primary btn-block" id="fin-calculate" type="button"
              style="padding: var(--ca-space-4);">
        Assess financeability
      </button>
    </div>

    <div id="financeability-results" hidden></div>
  `;

  const applied = prefillForm(page, {
    annualRevenue: 'fin-revenue',
    dso: 'fin-dso',
    dpo: 'fin-dpo',
    dutyFloatDays: 'fin-duty-float',
    fleetAge: 'fin-fleet-age',
    topClientShare: 'fin-concentration'
  });
  if (applied.length > 0) {
    const notice = PrefillNotice(applied, page);
    if (notice) page.querySelector('#fin-form').prepend(notice);
  }

  page.querySelector('#fin-calculate').addEventListener('click', () => calculate(page));
  return page;
}

function calculate(page) {
  const val = (id) => parseNumber(page.querySelector(`#${id}`).value);
  const checked = (id) => page.querySelector(`#fin-${id}`).checked;

  const input = {
    annualRevenue: val('fin-revenue'),
    ebitda: val('fin-ebitda'),
    topClientShare: val('fin-concentration'),
    dso: val('fin-dso') ?? 0,
    dpo: val('fin-dpo') ?? 0,
    dutyFloatDays: val('fin-duty-float') ?? 0,
    annualDebtService: val('fin-debt-service') ?? 0,
    fleetAge: val('fin-fleet-age'),
    auditedAccounts: checked('auditedAccounts'),
    managementAccounts: checked('managementAccounts'),
    assetRegister: checked('assetRegister'),
    complianceCurrent: checked('complianceCurrent')
  };

  const errors = [];
  if (input.annualRevenue === null || input.annualRevenue <= 0)
    errors.push('Enter annual revenue greater than zero.');
  if (input.ebitda === null) errors.push('Enter annual EBITDA — a negative figure is fine and informative.');
  if (input.dso === 0 && input.dpo === 0 && input.dutyFloatDays === 0)
    errors.push('Enter at least one of debtor days, creditor days or duty float.');

  const errorSlot = page.querySelector('#fin-error');
  if (errors.length > 0) {
    errorSlot.innerHTML = html`
      <div class="notice notice-danger" style="margin-bottom: var(--ca-space-4);">
        <span style="flex-shrink:0;margin-top:2px;">${raw(icon('alert', 15))}</span>
        <div>
          <strong style="font-weight: var(--ca-weight-medium);">Cannot assess yet</strong>
          <ul style="margin: var(--ca-space-1) 0 0 var(--ca-space-4); font-size: var(--ca-text-xs);">
            ${errors.map((e) => html`<li>${e}</li>`)}
          </ul>
        </div>
      </div>
    `;
    errorSlot.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    return;
  }
  errorSlot.innerHTML = '';

  const result = assessFinanceability(input);
  renderResults(page, input, result);

  void saveRecord('financeability', {
    score: result.score,
    dscr: Number.isFinite(result.dscr) ? result.dscr : null,
    ccc: result.ccc,
    gap: result.gap
  });

  rememberProfile({
    annualRevenue: input.annualRevenue,
    dso: input.dso,
    dpo: input.dpo,
    dutyFloatDays: input.dutyFloatDays,
    fleetAge: input.fleetAge,
    topClientShare: input.topClientShare
  });
}

function renderResults(page, input, r) {
  page.querySelector('#fin-form').hidden = true;

  const results = page.querySelector('#financeability-results');
  results.hidden = false;
  results.className = 'animate-fade-in';
  results.replaceChildren();

  const weakest = r.dimensions[0];

  /* ---- Headline ---- */
  const head = document.createElement('div');
  head.style.cssText = 'text-align:center; margin-bottom: var(--ca-space-6);';
  head.innerHTML = html`
    <div class="badge" style="background: color-mix(in srgb, ${r.verdict.tone} 13%, transparent); color:${readable(r.verdict.tone)};">
      ${r.verdict.label.toUpperCase()}
    </div>
    <div class="stat-figure num" style="margin-top: var(--ca-space-3); color:${readable(r.verdict.tone)};">
      ${r.score}
    </div>
    <div style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary); margin-top: var(--ca-space-1);">
      financeability out of 100
    </div>
    <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin-top: var(--ca-space-3); max-width: 46ch; margin-inline: auto;">
      ${r.verdict.note}
    </p>
  `;
  results.appendChild(head);

  /* ---- The three numbers a lender asks for ---- */
  const headline = document.createElement('div');
  headline.className = 'card';
  headline.style.marginBottom = 'var(--ca-space-4)';
  headline.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-4);">
      The three numbers they will ask for
    </h3>
    <div class="stack" style="gap: var(--ca-space-4);">
      <div>
        <div class="row" style="align-items: baseline;">
          <span class="stat-figure num" style="font-size: var(--ca-text-xl); color:${readable(r.dscrBand.tone)};">
            ${Number.isFinite(r.dscr) ? number(r.dscr, 2) : '—'}
          </span>
          <span class="badge" style="background: color-mix(in srgb, ${r.dscrBand.tone} 13%, transparent); color:${readable(r.dscrBand.tone)};">
            ${r.dscrBand.label}
          </span>
        </div>
        <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
          Debt service coverage ratio. ${r.dscrBand.note}
        </div>
      </div>

      <div>
        <div class="stat-figure num" style="font-size: var(--ca-text-xl);">
          ${number(r.ccc)} days
        </div>
        <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
          Cash conversion cycle — <span class="num">${number(input.dso)}</span> debtor days
          + <span class="num">${number(input.dutyFloatDays)}</span> duty float
          − <span class="num">${number(input.dpo)}</span> creditor days.
        </div>
      </div>

      <div>
        <div class="stat-figure num" style="font-size: var(--ca-text-xl); color: ${readable(r.gap > 0 ? 'var(--ca-danger)' : 'var(--ca-positive)')};">
          ${money(Math.abs(r.gap))}
        </div>
        <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
          ${r.gap > 0
            ? 'locked in the cycle at any moment — this is the size of the facility the operation actually needs'
            : 'released by the cycle — suppliers are funding the operation'}
        </div>
      </div>
    </div>
  `;
  results.appendChild(headline);

  /* ---- Dimensions ---- */
  const dims = document.createElement('div');
  dims.className = 'card';
  dims.style.marginBottom = 'var(--ca-space-4)';
  dims.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-3);">How the file reads</h3>
    <div class="stack" style="gap: var(--ca-space-3);">
      ${DIMENSIONS.map((d) => {
        const score = r.scores[d.id];
        const tone =
          score >= 70 ? 'var(--ca-positive)' : score >= 45 ? 'var(--ca-warning)' : 'var(--ca-danger)';
        return html`
          <div>
            <div class="row" style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-1);">
              <span style="flex:1;">${d.label}</span>
              <span class="num" style="color: var(--ca-text-quaternary); font-size: var(--ca-text-xs);">
                ${d.weight}% weight
              </span>
              <span class="num" style="width:38px; text-align:right; color:${readable(tone)}; font-weight: var(--ca-weight-medium);">
                ${score}
              </span>
            </div>
            <div class="progress-track">
              <div class="progress-fill" style="width:${score}%; background:${tone};"></div>
            </div>
          </div>
        `;
      })}
    </div>
  `;
  results.appendChild(dims);

  /* ---- The binding constraint ---- */
  const remedy = document.createElement('div');
  remedy.className = 'card';
  remedy.style.marginBottom = 'var(--ca-space-4)';
  remedy.style.borderColor = 'color-mix(in srgb, var(--brand-accent) 30%, var(--ca-border))';
  remedy.innerHTML = html`
    <div class="row" style="margin-bottom: var(--ca-space-3);">
      <span style="color: var(--brand-accent-text);">${raw(icon('pin', 18))}</span>
      <h3 style="flex:1; font-size: var(--ca-text-sm);">
        What is actually holding this back: ${weakest.label.toLowerCase()}
      </h3>
    </div>
    <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">
      ${REMEDIES[weakest.id]}
    </p>
  `;
  results.appendChild(remedy);

  /* ---- Duty facility sizing, where it bites ---- */
  if (input.dutyFloatDays > 0 && r.gap > 0) {
    const dutyShare = input.dutyFloatDays / Math.max(1, r.ccc);
    const dutyPortion = r.gap * Math.min(1, Math.max(0, dutyShare));

    const duty = document.createElement('div');
    duty.className = 'notice notice-warning';
    duty.style.marginBottom = 'var(--ca-space-4)';
    duty.innerHTML = html`
      <span style="flex-shrink:0;margin-top:2px;">${raw(icon('coins', 15))}</span>
      <div style="font-size: var(--ca-text-xs);">
        <strong style="font-weight: var(--ca-weight-medium);">
          Roughly ${money(dutyPortion)} of the gap is duty float alone.
        </strong>
        That portion is addressable without a general working capital facility — a KRA duty
        credit arrangement moves the outflow off day zero, and is usually a shorter conversation
        than a term loan.
      </div>
    `;
    results.appendChild(duty);
  }

  /* ---- Missing documents ---- */
  const missing = LENDER_CHECKLIST.filter((item) => !input[item.id]);
  if (missing.length > 0) {
    const docs = document.createElement('div');
    docs.className = 'card';
    docs.style.marginBottom = 'var(--ca-space-4)';
    docs.innerHTML = html`
      <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-3);">
        Before the meeting
      </h3>
      <div class="stack" style="gap: var(--ca-space-2);">
        ${missing.map(
          (item) => html`
            <div class="row" style="font-size: var(--ca-text-sm); gap: var(--ca-space-2);">
              <span style="color: var(--ca-danger); flex-shrink:0;">${raw(icon('close', 14))}</span>
              <span style="flex:1;">${item.label}</span>
              <span style="font-size: var(--ca-text-2xs); color: var(--ca-text-quaternary);">${item.weight}</span>
            </div>
          `
        )}
      </div>
    `;
    results.appendChild(docs);
  }

  /* ---- Disclaimer ---- */
  const disclaimer = document.createElement('div');
  disclaimer.className = 'card card-tinted';
  disclaimer.innerHTML = html`
    <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
      <strong style="font-weight: var(--ca-weight-medium); color: var(--ca-text-secondary);">
        This is not a credit decision.
      </strong>
      It is the arithmetic a lender will run in the first ten minutes, done in advance so you are
      not hearing it for the first time across the table. Every figure comes from what you
      entered; none of it has been verified, and none of it has left this device.
    </p>
  `;
  results.appendChild(disclaimer);

  results.appendChild(
    PrintButton({
      targetId: 'financeability-results',
      title: 'Financeability Assessment',
      hintText:
        'Adds the figures this assessment rests on, and what was not declared — the first ' +
        'thing a lender asks about a DSCR.',
      subtitle: `Score ${r.score}/100 · ${r.verdict.label} · DSCR ${Number.isFinite(r.dscr) ? number(r.dscr, 2) : 'n/a'} · CCC ${number(r.ccc)} days`,
      /* No basis section. A financeability score is measured against
         thresholds inside its own engine, not against a published band,
         and a "Basis of comparison" heading with nothing under it reads
         as "we checked and found no gap". */
      buildPreamble: () =>
        BoardPreamble(r, {
          input,
          register: FINANCEABILITY_REGISTER,
          prefix: 'KLF',
          contentHeading: 'Assessment'
        }),
      footnote: boardFootnote(r, { input, register: FINANCEABILITY_REGISTER, prefix: 'KLF' })
    })
  );

  const again = document.createElement('div');
  again.className = 'no-print';
  again.style.cssText = 'text-align:center; margin-top: var(--ca-space-4);';
  const btn = document.createElement('button');
  btn.className = 'btn btn-ghost';
  btn.type = 'button';
  btn.textContent = 'Change the inputs';
  btn.addEventListener('click', () => {
    results.hidden = true;
    page.querySelector('#fin-form').hidden = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
  again.appendChild(btn);
  results.appendChild(again);

  results.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export { percent };
